import React from 'react';
import PropTypes from 'prop-types';
import { FormControl, InputLabel, Select, MenuItem, CircularProgress, ListSubheader } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  formControl: {
    minWidth: 250,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
  },
}));

const TriggerFieldSelect = ({
  value,
  onChange,
  fields,
  isLoading,
  label,
  required,
  disabled,
  helperText,
}) => {
  const classes = useStyles();

  if (isLoading) {
    return (
      <FormControl className={classes.formControl} disabled>
        <InputLabel>{label}</InputLabel>
        <Select value="">
          <MenuItem value="">
            <div className={classes.loadingContainer}>
              <CircularProgress size={16} />
              <span>Loading fields...</span>
            </div>
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  const handleChange = (e) => {
    const rawValue = e.target.value;
    // Clicking a ListSubheader inside a MUI v4 Select fires onChange with an undefined value; ignore it
    if (rawValue === undefined) return;
    const id = rawValue === '' ? null : rawValue;
    const field = id === null ? null : fields.find(f => f.validTriggerFieldId === id) || null;
    onChange(id, field);
  };

  const renderFieldItem = (field) => (
    <MenuItem key={field.validTriggerFieldId} value={field.validTriggerFieldId}>
      {field.displayName}
    </MenuItem>
  );

  // Fields arrive ordered File table first, then by table display name, then display name.
  // When more than one table is present, group them under a subheader. MUI v4 Select rejects
  // Fragments as children, so the options are rendered as one FLAT array of elements.
  const renderOptions = () => {
    const tableDisplayNames = [];
    fields.forEach((field) => {
      if (!tableDisplayNames.includes(field.tableDisplayName)) {
        tableDisplayNames.push(field.tableDisplayName);
      }
    });

    if (tableDisplayNames.length <= 1) {
      return fields.map(renderFieldItem);
    }

    const items = [];
    tableDisplayNames.forEach((tableDisplayName) => {
      items.push(
        <ListSubheader key={`group-${tableDisplayName}`} disableSticky>
          {tableDisplayName}
        </ListSubheader>,
      );
      fields
        .filter(f => f.tableDisplayName === tableDisplayName)
        .forEach(field => items.push(renderFieldItem(field)));
    });
    return items;
  };

  return (
    <FormControl className={classes.formControl} required={required} disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select value={value ?? ''} onChange={handleChange}>
        <MenuItem value="">
          <em>Select a field</em>
        </MenuItem>
        {renderOptions()}
      </Select>
      {helperText && (
        <p style={{ fontSize: '0.75rem', color: '#666', marginTop: 4 }}>{helperText}</p>
      )}
    </FormControl>
  );
};

TriggerFieldSelect.propTypes = {
  value: PropTypes.number,
  // onChange(validTriggerFieldId | null, fieldObject | null)
  onChange: PropTypes.func.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      validTriggerFieldId: PropTypes.number,
      fieldName: PropTypes.string,
      displayName: PropTypes.string,
      tableName: PropTypes.string,
      tableDisplayName: PropTypes.string,
      dataType: PropTypes.string,
      isSegmentTable: PropTypes.bool,
      isFileTable: PropTypes.bool,
    }),
  ),
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  helperText: PropTypes.string,
};

TriggerFieldSelect.defaultProps = {
  value: null,
  fields: [],
  isLoading: false,
  label: 'Trigger Field',
  required: false,
  disabled: false,
  helperText: '',
};

export default TriggerFieldSelect;
